import React, { memo, useRef, useState } from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { ApiStakingState } from '../../api/types';
import type { Theme } from '../../global/types';

import { IS_EXPLORER, IS_FEATURE_LIMITED, NO_AGENT_AND_EXPLORE } from '../../config';
import {
  selectAccountStakingState,
  selectCurrentAccountId,
  selectCurrentAccountSettings,
  selectCurrentAccountState,
  selectDefaultOffRampChain,
  selectDefaultOnRampChain,
  selectIsCurrentAccountViewMode,
  selectIsOffRampAllowed,
  selectIsStakingDisabled,
  selectIsSwapDisabled,
} from '../../global/selectors';
import { useAccentColor } from '../../util/accentColor';
import buildClassName from '../../util/buildClassName';
import { getStakingStateStatus } from '../../util/staking';
import { IS_ELECTRON } from '../../util/windowEnvironment';
import { calcSafeAreaTop } from './helpers/calcSafeAreaTop';

import useAppTheme from '../../hooks/useAppTheme';
import useBackgroundMode, { isBackgroundModeActive } from '../../hooks/useBackgroundMode';
import { useDeviceScreen } from '../../hooks/useDeviceScreen';
import useEffectOnce from '../../hooks/useEffectOnce';
import useElementVisibility from '../../hooks/useElementVisibility';
import useFlag from '../../hooks/useFlag';
import useInterval from '../../hooks/useInterval';
import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';
import usePreventPinchZoomGesture from '../../hooks/usePreventPinchZoomGesture';

import LinkingDomainModal from '../domain/LinkingDomainModal';
import RenewDomainModal from '../domain/RenewDomainModal';
import InvoiceModal from '../receive/InvoiceModal';
import ReceiveModal from '../receive/ReceiveModal';
import StakeModal from '../staking/StakeModal';
import StakingClaimModal from '../staking/StakingClaimModal';
import StakingInfoModal from '../staking/StakingInfoModal';
import UnstakeModal from '../staking/UnstakeModal';
import Transition from '../ui/Transition';
import UpdateAvailable from '../ui/UpdateAvailable';
import VestingModal from '../vesting/VestingModal';
import VestingPasswordModal from '../vesting/VestingPasswordModal';
import MainSkeleton from './MainSkeleton';
import AccountSelectorModal from './modals/accountSelector/AccountSelectorModal';
import PromotionModal from './modals/PromotionModal';
import {
  LandscapeNavBar,
  LandscapeWalletList,
  PortraitActions,
} from './sections/Actions';
import PromoteWallet from './sections/Actions/PromoteWallet';
import Card from './sections/Card';
import PortraitContent from './sections/Content/PortraitContent';
import Header from './sections/Header/Header';
import LandscapeLayout from './sections/LandscapeLayout';
import Warnings from './sections/Warnings';

import styles from './Main.module.scss';

interface OwnProps {
  isActive?: boolean;
}

type StateProps = {
  currentTokenSlug?: string;
  stakingState?: ApiStakingState;
  isTestnet?: boolean;
  isViewMode: boolean;
  isStakingInfoModalOpen?: boolean;
  isSwapDisabled?: boolean;
  isStakingDisabled?: boolean;
  isOnRampDisabled?: boolean;
  isOffRampAllowed?: boolean;
  isMediaViewerOpen?: boolean;
  isAppReady?: boolean;
  theme: Theme;
  accentColorIndex?: number;
};

const UPDATE_SWAPS_INTERVAL_NOT_FOCUSED = 15000; // 15 sec
const UPDATE_SWAPS_INTERVAL = 3000; // 3 sec

function Main({
  isActive,
  currentTokenSlug,
  stakingState,
  isTestnet,
  isViewMode,
  isStakingInfoModalOpen,
  isSwapDisabled,
  isStakingDisabled,
  isOnRampDisabled,
  isOffRampAllowed,
  isMediaViewerOpen,
  isAppReady,
  theme,
  accentColorIndex,
}: OwnProps & StateProps) {
  const {
    openBackupWalletModal,
    closeStakingInfo,
    openStakingInfoOrStart,
    changeCurrentStaking,
    loadExploreSites,
    updatePendingSwaps,
  } = getActions();

  const lang = useLang();
  const cardRef = useRef<HTMLDivElement>();
  const portraitContainerRef = useRef<HTMLDivElement>();
  const landscapeContainerRef = useRef<HTMLDivElement>();

  const safeAreaTop = calcSafeAreaTop();
  const [isFocused, markIsFocused, unmarkIsFocused] = useFlag(!isBackgroundModeActive());
  const [areTabsStuck, setAreTabsStuck] = useState(false);
  const intersectionRootMarginTop = safeAreaTop;

  const stakingStatus = stakingState ? getStakingStateStatus(stakingState) : 'inactive';

  useBackgroundMode(unmarkIsFocused, markIsFocused);

  usePreventPinchZoomGesture(isMediaViewerOpen);

  const { isPortrait, isLandscape } = useDeviceScreen();

  useEffectOnce(() => {
    if (IS_FEATURE_LIMITED || NO_AGENT_AND_EXPLORE) return;

    loadExploreSites({ isLandscape, langCode: lang.code });
  });

  useInterval(updatePendingSwaps, isFocused ? UPDATE_SWAPS_INTERVAL : UPDATE_SWAPS_INTERVAL_NOT_FOCUSED);

  // Use scroll detection for portrait mode
  const { isVisible: isPageAtTop } = useElementVisibility({
    isDisabled: !isPortrait || !isActive,
    targetRef: cardRef,
    rootMargin: `-${intersectionRootMarginTop}px 0px 0px 0px`,
    threshold: [1],
  });

  const { isVisible: shouldHideBalanceInHeader } = useElementVisibility({
    isDisabled: !isPortrait || !isActive,
    targetRef: cardRef,
    rootMargin: `-${intersectionRootMarginTop}px 0px 0px 0px`,
  });

  const appTheme = useAppTheme(theme);
  useAccentColor(isPortrait ? portraitContainerRef : landscapeContainerRef, appTheme, accentColorIndex);

  const handleEarnClick = useLastCallback((stakingId?: string) => {
    if (stakingId) changeCurrentStaking({ stakingId });

    openStakingInfoOrStart();
  });

  function renderPortraitLayout() {
    return (
      <div ref={portraitContainerRef} className={styles.portraitContainer}>
        <div className={styles.head}>
          <Header
            withBalance={!shouldHideBalanceInHeader}
            areTabsStuck={areTabsStuck}
            isScrolled={!isPageAtTop}
          />

          <Card ref={cardRef} onYieldClick={handleEarnClick} />

          <Warnings onOpenBackupWallet={openBackupWalletModal} />

          {!isViewMode && (
            <PortraitActions
              tokenSlug={currentTokenSlug}
              containerRef={portraitContainerRef}
              isTestnet={isTestnet}
              stakingStatus={stakingStatus}
              isStakingDisabled={isStakingDisabled}
              isSwapDisabled={isSwapDisabled}
              isOnRampDisabled={isOnRampDisabled}
              isOffRampDisabled={!isOffRampAllowed}
              onEarnClick={handleEarnClick}
            />
          )}
        </div>

        <PortraitContent
          isActive={isActive}
          onStakedTokenClick={handleEarnClick}
          onTabsStuck={setAreTabsStuck}
        />
      </div>
    );
  }

  function renderLandscapeLayout() {
    return (
      <div ref={landscapeContainerRef} className={styles.landscapeContainer} dir={lang.isRtl ? 'rtl' : 'ltr'}>
        <div className={buildClassName(styles.sidebar, 'custom-scroll')}>
          <Warnings onOpenBackupWallet={openBackupWalletModal} />

          <Header />

          <Card onYieldClick={handleEarnClick} />

          <LandscapeNavBar />
          {/* Core is single-account, and its `Add Wallet` would be dead anyway: AccountSelectorModal is not rendered below. */}
          {!IS_FEATURE_LIMITED && <LandscapeWalletList />}
          {IS_EXPLORER && <PromoteWallet />}
        </div>
        <div className={styles.main}>
          <LandscapeLayout onStakedTokenClick={handleEarnClick} />
        </div>
      </div>
    );
  }

  function renderContent() {
    if (IS_EXPLORER) {
      return (
        <Transition name="semiFade" activeKey={isAppReady ? 1 : 0}>
          {isAppReady
            ? (isPortrait ? renderPortraitLayout() : renderLandscapeLayout())
            : <MainSkeleton isViewMode={isViewMode} />}
        </Transition>
      );
    }

    return isPortrait ? renderPortraitLayout() : renderLandscapeLayout();
  }

  return (
    <>
      {renderContent()}

      <StakeModal />
      <StakingInfoModal isOpen={isStakingInfoModalOpen} onClose={closeStakingInfo} />
      <ReceiveModal />
      <InvoiceModal />
      <UnstakeModal />
      <StakingClaimModal />
      <VestingModal />
      <VestingPasswordModal />
      <RenewDomainModal />
      <LinkingDomainModal />
      <PromotionModal />
      {!IS_ELECTRON && <UpdateAvailable />}
      {!IS_FEATURE_LIMITED && <AccountSelectorModal />}
    </>
  );
}

export default memo(
  withGlobal<OwnProps>(
    (global): StateProps => {
      const currentAccountId = selectCurrentAccountId(global);
      const accountState = selectCurrentAccountState(global);
      const { isAppReady } = accountState ?? {};

      const stakingState = currentAccountId
        ? selectAccountStakingState(global, currentAccountId)
        : undefined;

      return {
        currentTokenSlug: accountState?.currentTokenSlug,
        stakingState,
        isTestnet: global.settings.isTestnet,
        isViewMode: selectIsCurrentAccountViewMode(global),
        isStakingInfoModalOpen: global.isStakingInfoModalOpen,
        isMediaViewerOpen: Boolean(global.mediaViewer?.mediaId),
        isSwapDisabled: selectIsSwapDisabled(global),
        isStakingDisabled: selectIsStakingDisabled(global),
        // Both labels stand for the ramps reachable from this account, so they read the very chain each would open
        isOnRampDisabled: !selectDefaultOnRampChain(global),
        isOffRampAllowed: selectIsOffRampAllowed(global, selectDefaultOffRampChain(global)),
        isAppReady,
        theme: global.settings.theme,
        accentColorIndex: selectCurrentAccountSettings(global)?.accentColorIndex,
      };
    },
    (global, _, stickToFirst) => stickToFirst(selectCurrentAccountId(global)),
  )(Main),
);
